import { useEffect } from "react";
import _ from "lodash";

import PusherManager from "utils/pusher-manager";

import { useSignedUser } from "entities/Auth/sdk";

export const useTableResponseReady = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler: ({
    tableResponseId,
  }: {
    tableResponseId: number;
  }) => void;
  failureEventHandler: () => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToTableResponseSucceededEvent({
        userId: user.id,
        eventHandler: ({
          openai_table_response_id,
        }: {
          openai_table_response_id: number;
        }) =>
          successEventHandler({
            tableResponseId: openai_table_response_id,
          }),
      });
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToTableResponseFailedEvent({
        userId: user.id,
        eventHandler: failureEventHandler,
      });
    }
  }, [user, failureEventHandler]);
};

export const useBulkDealImportReady = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler: () => void;
  failureEventHandler?: ({ detail }: { detail: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isNil(user)) {
      return PusherManager.bindToBulkDealImportSucceededEvent({
        userId: user.id,
        eventHandler: successEventHandler,
      });
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(failureEventHandler)) {
      return PusherManager.bindToBulkDealImportFailedEvent({
        userId: user.id,
        eventHandler: ({ detail }) => failureEventHandler({ detail }),
      });
    }
  }, [user, failureEventHandler]);
};

export const useChecklistFileImportReady = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler?: ({
    company_package_file_import_id,
  }: {
    company_package_file_import_id: number;
  }) => void;
  failureEventHandler?: ({ detail }: { detail: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(successEventHandler)) {
      return PusherManager.bindToChecklistFileImportSucceededEvent({
        userId: user.id,
        eventHandler: successEventHandler,
      });
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(failureEventHandler)) {
      return PusherManager.bindToChecklistFileImportFailedEvent({
        userId: user.id,
        eventHandler: ({ detail }) => failureEventHandler({ detail }),
      });
    }
  }, [user, failureEventHandler]);
};

export const useCompanySectionAutomaticFillProcessing = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler?: ({
    dealId,
    packageId,
    tabId,
    companySectionAutomaticFillId,
  }: {
    dealId: number;
    packageId: number;
    tabId: number;
    companySectionAutomaticFillId: number;
  }) => void;
  failureEventHandler?: ({ error }: { error: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(successEventHandler)) {
      return PusherManager.bindToCompanySectionAutomaticFillProcessingSuccededEvent(
        {
          userId: user.id,
          eventHandler: ({
            deal_id,
            company_package_id,
            automatic_fill_id,
            company_tab_id,
          }: {
            deal_id: number;
            company_package_id: number;
            automatic_fill_id: number;
            company_tab_id: number;
          }) =>
            successEventHandler({
              dealId: deal_id,
              packageId: company_package_id,
              tabId: company_tab_id,
              companySectionAutomaticFillId: automatic_fill_id,
            }),
        }
      );
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(failureEventHandler)) {
      return PusherManager.bindToCompanySectionAutomaticFillProcessingFailedEvent(
        {
          userId: user.id,
          eventHandler: ({ detail }) => failureEventHandler({ error: detail }),
        }
      );
    }
  }, [user, failureEventHandler]);
};

export const useMemoSectionAIExtractionProcessing = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler?: ({
    dealId,
    memoId,
  }: {
    dealId: number;
    memoId: number;
    memoSectionId: number;
  }) => void;
  failureEventHandler?: ({ error }: { error: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(successEventHandler)) {
      return PusherManager.bindToMemoSectionAIExtractionProcessingSuccededEvent(
        {
          userId: user.id,
          eventHandler: ({
            deal_id,
            memo_id,
            memo_section_id,
          }: {
            deal_id: number;
            memo_id: number;
            memo_section_id: number;
          }) =>
            successEventHandler({
              dealId: deal_id,
              memoId: memo_id,
              memoSectionId: memo_section_id,
            }),
        }
      );
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(failureEventHandler)) {
      return PusherManager.bindToMemoSectionAIExtractionProcessingFailedEvent({
        userId: user.id,
        eventHandler: ({ detail }) => failureEventHandler({ error: detail }),
      });
    }
  }, [user, failureEventHandler]);
};

export const useCompanyPackageSnapshotProcessing = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler?: () => void;
  failureEventHandler?: ({ detail }: { detail: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(successEventHandler)) {
      return PusherManager.bindToCompanyPackageSnapshotProcessingSucceededEvent(
        {
          userId: user.id,
          eventHandler: successEventHandler,
        }
      );
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(failureEventHandler)) {
      return PusherManager.bindToCompanyPackageSnapshotProcessingFailedEvent({
        userId: user.id,
        eventHandler: failureEventHandler,
      });
    }
  }, [user, failureEventHandler]);
};

export const useZipProcessing = ({
  successEventHandler,
  failureEventHandler,
}: {
  successEventHandler?: () => void;
  failureEventHandler?: () => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(successEventHandler)) {
      return PusherManager.bindToZipReadyEvent({
        userId: user.id,
        eventHandler: successEventHandler,
      });
    }
  }, [user, successEventHandler]);

  useEffect(() => {
    if (!_.isNil(user) && !_.isNil(failureEventHandler)) {
      return PusherManager.bindToZipFailedEvent({
        userId: user.id,
        eventHandler: failureEventHandler,
      });
    }
  }, [user, failureEventHandler]);
};
export const useDealTableUpdateSucceeded = ({
  successEventHandler,
}: {
  successEventHandler: ({
    dealId,
    tableId,
    tableName,
  }: {
    dealId: number;
    tableId: number;
    tableName: string;
  }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToDealTableUpdateSucceededEvent({
        userId: user.id,
        eventHandler: ({ deal_id, table_id, table_name }) =>
          successEventHandler({
            dealId: deal_id,
            tableId: table_id,
            tableName: table_name,
          }),
      });
    }
  }, [user, successEventHandler]);
};

export const useTableUpdateSucceeded = ({
  successEventHandler,
}: {
  successEventHandler: ({
    tableId,
    tableName,
  }: {
    tableId: number;
    tableName: string;
  }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToTableUpdateSucceededEvent({
        userId: user.id,
        eventHandler: ({ table_id, table_name }) =>
          successEventHandler({
            tableId: table_id,
            tableName: table_name,
          }),
      });
    }
  }, [user, successEventHandler]);
};

export const useTableOrDealTableUpdateFailed = ({
  failureEventHandler,
}: {
  failureEventHandler: ({ details }: { details: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToTableOrDealTableUpdateFailedEvent({
        userId: user.id,
        eventHandler: ({ details }) =>
          failureEventHandler({
            details,
          }),
      });
    }
  }, [user, failureEventHandler]);
};

export const useDealCompanyImportTableSheetNameNotFound = ({
  eventHandler,
}: {
  eventHandler: ({ details }: { details: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToDealCompanyImportTableSheetNameNotFoundEvent({
        userId: user.id,
        eventHandler,
      });
    }
  }, [user, eventHandler]);
};

export const useDealCompanyImportTableUpdateFailed = ({
  eventHandler,
}: {
  eventHandler: ({ details }: { details: string }) => void;
}) => {
  const { data: user } = useSignedUser();

  useEffect(() => {
    if (!_.isUndefined(user)) {
      return PusherManager.bindToDealCompanyImportTableUpdateFailedEvent({
        userId: user.id,
        eventHandler,
      });
    }
  }, [user, eventHandler]);
};
